import { useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  isMultiSelectedAtomFamily,
  isOnlyNodeSelectedAtomFamily,
  shouldShowStepIdsAtom,
  zapStepNodesAtomFamily,
  shouldShowAddStepButtonAtom,
  ZapNodeData,
  isViewOnlyModeAtom,
  loadableZapMetadataAsyncAtomFamily,
  loadableAssetAccessAsyncAtomFamily,
  ZapMetadata,
  isZapErrorObject,
  areHandlesVisibleAtomFamily,
  NodeType,
  isValidUUID,
  hasSourceNodeAtomFamily,
  hasVisibleAnnotationAtomFamily,
  nodeIdToExplainAtom,
  isDraftNotTemplateNodeAtomFamily,
} from "@zapier/canvas-state";
import classNames from "classnames";
import { Node, NodeProps } from "@xyflow/react";
import { copilotLoadingNodeIdAtom } from "@zapier/copilot-tools";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";

import { AddStepButton } from "../../../../components/AddStepButton";
import { getDefaultBackgroundColorForAssetType } from "../../../../utils/getDefaultBackgroundColorForAssetType";
import { usePreviewContext } from "../../../../context/PreviewProvider";
import { useAssetPreload } from "../../../../hooks/useAssetPreload";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { getDefaultColorForAssetType } from "../../../../utils/getDefaultColorForAssetType";
import { StepId } from "../../../StepId";
import { FloatingInlineToolbar } from "../../../FloatingInlineToolbar";
import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import { CanvasCopilotIndicator } from "../../../CanvasCopilotIndicator";
import { AssetToggleOrStatus } from "../../../AssetToggleOrStatus";

import { ZapNodeContent } from "./ZapNodeContent";
import { LoadingZapNode } from "./Loader";
import styles from "./ZapNode.module.css";

export function ZapNode(props: NodeProps<Node<ZapNodeData>>) {
  const { data, id, type } = props;
  const isDraftNotTemplateNode = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(id),
  );
  const {
    zapId,
    color = getDefaultColorForAssetType({
      assetType: "zap",
      assetId: zapId,
      isDraftNotTemplateNode,
    }),
  } = data;

  const backgroundColor = getDefaultBackgroundColorForAssetType({
    assetType: "zap",
    assetId: zapId,
    color: data.color, // we need actual color without fallback from getDefaultColorForAssetType
    isDraftNotTemplateNode,
  });

  const loadableData = useAtomValue(loadableZapMetadataAsyncAtomFamily(zapId));
  const fetchedZap =
    loadableData.state === "hasData" ? loadableData.data : undefined;
  const isLoadingZap = loadableData.state === "loading";
  const isZapNotFound =
    loadableData.state === "hasError" ||
    Boolean(fetchedZap && isZapErrorObject(fetchedZap));
  const zap =
    !fetchedZap || isZapNotFound ? undefined : (fetchedZap as ZapMetadata);

  const loadableZapAccessData = useAtomValue(
    loadableAssetAccessAsyncAtomFamily({
      assetId: zapId,
      assetType: "zap",
    }),
  );
  const zapAccess =
    loadableZapAccessData.state === "hasData"
      ? loadableZapAccessData.data
      : undefined;
  // Zap is forbidden when user lacks permission (403) but zap exists
  const isZapForbidden = zapAccess?.isAssetForbidden;
  const isLoadingZapAccess = loadableZapAccessData.state === "loading";
  const isLoading = isLoadingZap || isLoadingZapAccess;

  const { isPreview } = usePreviewContext();
  const hasSourceNode = useAtomValue(hasSourceNodeAtomFamily(id));
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const isMultiSelected = useAtomValue(isMultiSelectedAtomFamily(id));
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const updateNodeAndSave = useUpdateNode();

  const setZapSteps = useSetAtom(zapStepNodesAtomFamily(id));

  const isSelected = useAtomValue(isOnlyNodeSelectedAtomFamily(id));
  const hasVisibleAnnotation = useAtomValue(hasVisibleAnnotationAtomFamily(id));
  const nodeIdToExplain = useAtomValue(nodeIdToExplainAtom);
  const isBeingExplained = nodeIdToExplain === id;

  const isToolbarVisible =
    !isViewOnlyMode &&
    isSelected &&
    isValidUUID(id) &&
    !isZapNotFound &&
    !isBeingExplained;

  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  const loadingNodeId = useAtomValue(copilotLoadingNodeIdAtom);

  const handleMouseEnter = useAssetPreload({
    assetType: "zap",
    zapId,
    zap,
  });

  useEffect(() => {
    // Need Zap to be defined since that will affect the Zap node height in order to prevent overlaps with Zap steps
    if (zap?.zap_id || isZapNotFound) {
      // Delay so that Zap node height is final before we lay out the Zap steps or else Zap steps will overlap with the Zap node
      setTimeout(setZapSteps, 0);
    }
  }, [setZapSteps, zap?.zap_id, isZapNotFound]);

  if (isLoading) {
    return (
      <LoadingZapNode id={id} color={color} backgroundColor={backgroundColor} />
    );
  }

  const hasNotes = !isRemirrorJSONEmpty(data.notesJson);

  const shouldShowCopilotIndicator = loadingNodeId === id;

  return (
    <>
      {shouldShowCopilotIndicator ? (
        <CanvasCopilotIndicator nodeId={id} />
      ) : (
        <AssetToggleOrStatus assetType="zap" assetId={zapId ?? null} />
      )}
      {!hasSourceNode && shouldShowAddStepButton && !isPreview && (
        <AddStepButton handleType="target" />
      )}
      {shouldShowAddStepButton && !isPreview && <AddStepButton />}
      <NodeHoverWrapper>
        {shouldShowStepIds && <StepId />}
        {isToolbarVisible && (
          <FloatingInlineToolbar
            color={color}
            actors={data.actors}
            hasNotes={hasNotes}
            hasZap={Boolean(zapId)}
            isZapExpanded={data.isExpanded}
            isZapNotFound={isZapNotFound}
            zap={zap}
            nodeType={type as NodeType}
            assetIds={{
              zapId,
            }}
            isAssetUnlinked={!zapId}
          />
        )}
        <ZapNodeContent
          {...props}
          data={{ ...data, color }}
          zap={zap}
          isZapNotFound={isZapNotFound}
          onMouseEnter={handleMouseEnter}
          onUpdateNode={(updateData) =>
            updateNodeAndSave({ data: updateData }, { shouldSave: true })
          }
          className={classNames(styles.wrapper, {
            [styles.wrapperMultiSelected]: isMultiSelected,
            [styles.wrapperSingleSelected]:
              !isPreview && (isSelected || hasVisibleAnnotation),
          })}
          isViewOnlyMode={isViewOnlyMode || isZapForbidden}
          backgroundColor={backgroundColor}
        />
        <Handles nodeType="zap" isVisible={areHandlesVisible && !isPreview} />
      </NodeHoverWrapper>
    </>
  );
}
